import * as React from 'react';
import Axios from 'src/services/axios';
import {
  reduce,
  find,
  sortBy,
  isNil,
  findIndex,
  defaultTo,
  get,
  isNaN,
  cloneDeep,
  isObject,
  mapValues,
  forEach,
  isString,
  last,
  isArray,
  isNumber,
} from 'lodash';
import { isEmpty, sum } from 'lodash/fp';
import ServiceContainer from 'src/ServiceContainer';
import * as globalMath from 'mathjs';
import { all } from 'mathjs';

import { ClientDataApi } from 'configuration-schemas/confdefnView';
import { BasicItem } from 'src/types/Scope';
import {
  ReceiptStyles as styles,
  MUIStyles,
  REC_ADJ_COLUMN_WIDTH,
  REC_ADJ_ROW_HEIGHT,
} from './ReceiptsAdjCalculator.styles';
import CardActions from '@material-ui/core/CardActions';
import AcceptButton from 'src/components/AcceptButton/AcceptButton';
import RejectButton from 'src/components/RejectButton/RejectButton';
import Renderer, { RendererOptions } from 'src/utils/Domain/Renderer';

import {
  ICellEditorParams,
  GridReadyEvent,
  EditableCallbackParams,
  GridApi,
  ValueGetterParams,
  ProcessCellForExportParams,
  ColSpanParams,
  ICellRendererParams,
  ValueSetterParams,
  ColDef,
  CellStyle,
  CellClassParams,
} from '@ag-grid-community/core';

import { Overlay, InputInteger } from 'src/common-ui/index';
import { maybeReturnNestData } from 'src/utils/Http/NestedDatas';
import { MuiThemeProvider, Tabs } from '@material-ui/core';
import { muiTheme } from 'src/utils/Style/Theme';
import { AppType } from 'configuration-schemas/general';
import { GetDataCalculation, ParamedCalc, executeCalculation } from 'src/utils/LibraryUtils/MathUtils';
import { errorToLoggingPayload } from 'src/services/loggingService';
import ExtendedDataGrid from 'src/components/ExtendedDataGrid/ExtendedDataGrid';
import { wrappedHeaderStyle } from 'src/components/ConfigurableGrid/ConfigurableGrid.styles';
import { classes } from 'typestyle';
import flowSheetGridStyles from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetGrid.styles';

export interface ReceiptsAdjCalculatorProps extends ICellEditorParams {
  dataApi: ClientDataApi;
  configApi: ClientDataApi;
  floorset?: string;
  isEditable: boolean;
}

export interface PercentageRuleHigherLower {
  percentageRule: 'HigherLower';
  referenceColumns: {
    [col: string]: {
      higher: string;
      lower: string;
    };
  };
}

export interface PercentageRules extends PercentageRuleHigherLower {}

interface SimpleReceiptConfig {
  columns: {
    text: string;
    dataIndex: string;
    renderer?: RendererOptions;
    editable?: boolean;
    editableByCalc?: string | ParamedCalc;
  }[];
  dataRules?: {
    percentage: PercentageRules;
  };
}

export interface ReceiptsAdjCalculatorState {
  sizes: WeekSizes[];
  invalidSizes: WeekSizes[];
  activeWeekIndex: number;
  saved: boolean;
  overlayWidth: number;
  overlayHeight: number;
  shouldSave: boolean;
  staticTotals: StaticTotals[];
  config: SimpleReceiptConfig;
  // `isSaving` is used to hold the modal while update goes through
  isSaving: boolean;
  isLoading: boolean;
}

export interface StaticTotals {
  final: number;
  system: number;
  weekNo: string;
}

export interface SizeDetail extends BasicItem {
  system: number | undefined;
  userAdj: AggregateValue;
  onOrder: number | undefined;
  onOrderRevision: AggregateValue;
  final: number | undefined;
  lastPublished: number | undefined;
  sizeattribute?: string;
  default_size_profile: number;
  isValidSize: boolean;
}

export interface WeekSizes {
  weekNo: string;
  receipts: SizeDetail[];
  total?: SizeDetail;
}

export type ConfigSizeDetail = SizeDetail & {
  userAdj: number;
  onOrderRevision: number;
};

export interface ClientSizeDetail extends SizeDetail {
  userAdj: AggregateValue;
  onOrderRevision: AggregateValue;
}

export interface AggregateValue {
  value: number | undefined;
  percentage: number;
}

export interface NumberFormatValues {
  floatValue: number;
  formattedValue: string;
  value: string;
}

export interface GridRow extends SizeDetail {
  dataPath: string[];
  rowType: 'group' | 'total' | 'size' | 'invalid-size';
}

interface ReceiptAdjData {
  channel: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any[];
  dc_finalqty: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dc_finrev: any;
  dc_last_pub: number;
  dc_onorder: number;
  dc_planqty: number;
  dc_publish: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dc_useradj: any;
  default_size_profile: number;
  description: string;
  expanded: boolean;
  id: string;
  index: number;
  is_locked: number;
  leaf: boolean;
  'member:week:description': string;
  'member:week:id': string;
  'member:week:name': string;
  name: string;
  product: string;
  sizeattribute: string;
  stylecolor: string;
  week: string;
  isvalid: number;
}

export default class TabbedReceiptsAdjCalculator extends React.Component<ReceiptsAdjCalculatorProps> {
  state: ReceiptsAdjCalculatorState;
  math = globalMath.create(all) as globalMath.MathJsStatic;
  gridApi: GridApi | null = null;

  private readonly emptyTotalRow: SizeDetail = {
    id: 'Total',
    name: 'Total',
    system: undefined,
    userAdj: {
      value: undefined,
      percentage: 0.0,
    },
    onOrder: undefined,
    onOrderRevision: {
      value: undefined,
      percentage: 0.0,
    },
    final: undefined,
    lastPublished: 0,
    default_size_profile: 0,
    isValidSize: true,
  };

  constructor(props: ReceiptsAdjCalculatorProps) {
    super(props);
    this.state = {
      sizes: [],
      invalidSizes: [],
      activeWeekIndex: 0,
      saved: false,
      overlayWidth: window.innerWidth,
      overlayHeight: window.innerHeight,
      shouldSave: true,
      isSaving: false,
      staticTotals: [],
      isLoading: true,
      config: {
        columns: [
          {
            text: 'System',
            dataIndex: 'system',
          },
          {
            text: 'Rec Override',
            dataIndex: 'userAdj',
          },
          {
            text: 'On Order',
            dataIndex: 'onOrder',
          },
          {
            text: 'OO Override',
            dataIndex: 'onOrderRevision',
          },
          {
            text: 'Final',
            dataIndex: 'final',
          },
          {
            text: 'Last Pub',
            dataIndex: 'lastPublished',
          },
        ],
      },
    };

    window.addEventListener('resize', this.resizeOverlay.bind(this));
  }

  resizeOverlay() {
    this.setState({
      overlayWidth: window.innerWidth,
      overlayHeight: window.innerHeight,
    });
  }

  isCancelAfterEnd() {
    return !this.state.shouldSave;
  }

  getNonStdEditableCols = () => {
    return this.state.config.columns.filter((col) => col.editable === true).map((col) => col.dataIndex);
  };

  convertSizeDetailToServerData = (size: SizeDetail) => {
    const editableColumns = this.getNonStdEditableCols();

    const values = reduce(
      editableColumns,
      (acc, key) => {
        // @ts-expect-error
        acc[key] = size[key].value;
        return acc;
      },
      {} as Record<string, any>,
    );
    return {
      dc_useradj: size.userAdj.value,
      dc_finrev: size.onOrderRevision.value,
      dc_isedited: !isNil(size.onOrderRevision.value) || !isNil(size.userAdj.value) ? 1 : 0,
      ...values,
    };
  };

  fetchConfig = async (configApi: ClientDataApi) => {
    try {
      const configResponse = await Axios.get(configApi.url);
      const config = maybeReturnNestData(configResponse.data) as SimpleReceiptConfig;
      this.setState({
        config,
      });
    } catch (e) {
      ServiceContainer.loggingService.error(
        `Could not get config ${configApi.url} for TabbedReceiptsAdjCalculator.`,
        errorToLoggingPayload(e),
      );
    }
  };

  componentDidMount() {
    (async () => {
      if (this.props.configApi) {
        await this.fetchConfig(this.props.configApi);
      }
      const floorset = this.props.floorset;
      const dataParams = this.props.dataApi.params
        ? this.props.dataApi.params
        : {
            appName: AppType.Assortment,
            productId: this.props.node.data['stylecolor'],
            timeId: floorset,
            defnId: 'ReceiptAdjustments',
          };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp: { data: any } = await Axios.get(this.props.dataApi.url, {
        params: dataParams,
      });
      if (!isString(resp.data) && isEmpty(resp.data.data)) {
        this.setState({
          sizes: [],
          invalidSizes: [],
          staticTotals: [],
          isLoading: false,
        });
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sizeData: any = isString(resp.data)
        ? await ServiceContainer.pivotService.deserialize(resp.data)
        : resp.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tempData: any[] = [];

      sortBy(sizeData, 'size_id').forEach((data: ReceiptAdjData) => {
        const keys = tempData.length > 0 ? tempData.map((item) => item.weekNo) : [];
        if (keys.indexOf(data.week) === -1) {
          tempData.push({
            weekNo: data.week,
            receipts: [data],
          });
        } else {
          find(tempData, ['weekNo', data.week]).receipts.push(data);
        }
      });

      // Convert server data to SizeDetail
      // default_size / total
      const finalData: WeekSizes[] = tempData.map((weekSize) => {
        return {
          weekNo: weekSize.weekNo,
          receipts: weekSize.receipts.map((receipt: ReceiptAdjData) => {
            return {
              system: receipt.dc_planqty,
              userAdj: {
                value: receipt.dc_useradj,
                percentage: undefined,
              },
              onOrder: receipt.dc_onorder,
              onOrderRevision: {
                value: receipt.dc_finrev, // TODO
                percentage: undefined,
              },
              final: receipt.dc_finalqty,
              lastPublished: receipt.dc_last_pub,
              isValidSize: receipt.isvalid === 0 ? false : true,
              ...receipt,
            };
          }),
        };
      });

      const invalidSizes = finalData.map((weekSize: WeekSizes) => {
        return {
          weekNo: weekSize.weekNo,
          receipts: weekSize.receipts.filter((item) => !item.isValidSize),
        };
      });
      const validSizes = finalData.map((weekSize: WeekSizes) => {
        return {
          weekNo: weekSize.weekNo,
          receipts: weekSize.receipts.filter((item) => item.isValidSize),
        };
      });

      const finalInvalidSizes = this.calculateInitialPercentages(invalidSizes);
      const finalFinalData = this.calculateInitialPercentages(validSizes);

      const staticTotals = finalFinalData.map((weekSize: WeekSizes) => {
        const totalSystem = sum(weekSize.receipts.map((r) => r.system));
        const totalFinal = sum(weekSize.receipts.map((r) => r.final));

        return {
          final: totalFinal,
          system: totalSystem,
          weekNo: weekSize.weekNo,
        };
      });

      this.setState({
        invalidSizes: finalInvalidSizes,
        sizes: finalFinalData,
        activeWeekIndex: 0,
        staticTotals,
        isLoading: false,
      });
    })();
  }

  /**
   * Sizes with a specific '0' for their percent contribution should never receive remainders,
   * as we've specifically designated them to recieve no units
   * the following ensure they don't get 1 floating unit when they have been defaulted to 0 in the
   * size profile
   */
  filterOutZeroPercentages = (percInfo: { perc: number; ind: number }): boolean => {
    return percInfo.perc !== 0.0;
  };

  /**
   * This function calculates unit distributions based on the newly edited total value, and the two properties configured in `dataRules.percentage` to be compared
   * in the `percentageRules` function (which is usually the size curve of the existing receipts, and the `default_size_profile`).
   *
   * For each disagg configured in `dataRules.percentage.referenceColumns`, each column can have a configured `higher` or `lower`.
   * The intent of each `higher` or `lower` value is to allow one percentage curve to disagg units up to the "lower" value, and when units are added
   * beyond that, the `higher` value takes over and distributes units from then on.
   *
   * The reason there are "high/low" values here, is because due to a request from INT-1889 users want,
   * when they're editing the total receipts for an option, additional units *up to* the existing receipts curve to use the receipts, then added units beyond that
   * should use the `default_size_profile`.
   */
  calculatePercentageHigherLower = (
    percentageRules: PercentageRuleHigherLower,
    receipts: SizeDetail[],
    colIndex: string,
    newTotal: number,
  ) => {
    const reference = percentageRules.referenceColumns[colIndex];
    // @ts-expect-error
    const lowTotal = sum(receipts.map((r) => r[reference.lower]));
    const lowTotalPerc = receipts.map((r, ind) => {
      if (lowTotal === 0 || isNaN(lowTotal)) {
        return {
          perc: 1 / receipts.length,
          ind,
        };
      }
      // @ts-expect-error
      const val = r[reference.lower];
      return {
        perc: val / lowTotal,
        ind,
      };
    });
    // @ts-expect-error
    const highTotal = sum(receipts.map((r) => r[reference.higher]));
    const highTotalPerc = receipts.map((r, ind) => {
      if (highTotal === 0 || isNaN(highTotal)) {
        return {
          perc: lowTotalPerc[ind].perc,
          ind,
        };
      }
      // @ts-expect-error
      const val = r[reference.higher];
      return {
        perc: val / highTotal,
        ind,
      };
    });
    const highRemainder = newTotal - lowTotal;
    let newLowTotal = newTotal;
    if (highRemainder > 0) {
      newLowTotal = lowTotal;
    }
    let tmpRemain = newLowTotal;
    const finalDistribution = receipts.map((_size, ind) => {
      const newValue = Math.floor(lowTotalPerc[ind].perc * newLowTotal);
      tmpRemain -= newValue;
      return newValue;
    });
    sortBy(lowTotalPerc, 'perc')
      .filter(this.filterOutZeroPercentages)
      .forEach((percInfo) => {
        if (tmpRemain > 0) {
          finalDistribution[percInfo.ind] += 1;
          tmpRemain -= 1;
        } else if (tmpRemain < 0) {
          finalDistribution[percInfo.ind] -= 1;
          tmpRemain += 1;
        }
      });
    if (highRemainder > 0) {
      // distribute remainder by highTotalPerc
      let tmpRemain = highRemainder;
      receipts.forEach((_size, ind) => {
        const newValue = Math.floor(highTotalPerc[ind].perc * highRemainder);
        tmpRemain -= newValue;
        finalDistribution[ind] += newValue;
      });
      sortBy(highTotalPerc, 'perc')
        .filter(this.filterOutZeroPercentages)
        .forEach((percInfo) => {
          if (tmpRemain > 0) {
            finalDistribution[percInfo.ind] += 1;
            tmpRemain -= 1;
          } else if (tmpRemain < 0) {
            finalDistribution[percInfo.ind] -= 1;
            tmpRemain += 1;
          }
        });
    }
    return finalDistribution;
  };

  calculateInitialPercentages = (sizes: WeekSizes[]) => {
    const nonStandardEdits = this.getNonStdEditableCols();
    return sizes.map((weekSize: WeekSizes) => {
      const totalSystem = sum(weekSize.receipts.map((r) => r.system));
      const totalOnOrder = sum(weekSize.receipts.map((r) => r.onOrder));
      const totalProfiles = sum(weekSize.receipts.map((r) => r.default_size_profile));

      const valueGet = (size: SizeDetail, key: keyof SizeDetail) => {
        return isObject(size[key]) ? size[key].value : size[key];
      };
      const newReceipts = weekSize.receipts.map((size) => {
        const systemVal = size.system ?? 0;
        const onOrderVal = size.onOrder ?? 0;
        // let percentageSystem: number = totalSystem !== 0 ? systemVal / totalSystem : size.default_size_profile;
        let percentageSystem;
        if (size.userAdj.percentage) {
          percentageSystem = size.userAdj.percentage;
        } else if (totalSystem !== 0) {
          percentageSystem = systemVal / totalSystem;
        } else if (totalProfiles > 0) {
          percentageSystem = size.default_size_profile / totalProfiles;
        } else {
          percentageSystem = 1 / weekSize.receipts.length;
        }

        let percentageOnOrder: number;
        if (size.onOrderRevision.percentage) {
          percentageOnOrder = size.onOrderRevision.percentage;
        } else if (totalOnOrder !== 0) {
          percentageOnOrder = onOrderVal / totalOnOrder;
        } else {
          percentageOnOrder = percentageSystem;
        }

        const percValues = forEach(nonStandardEdits, (key) => {
          // @ts-expect-error
          if (!isObject(size[key])) {
            // @ts-expect-error
            size[key] = {
              // @ts-expect-error
              value: size[key],
              percentage: 0,
            };
          }
          const rules = this.state.config.dataRules?.percentage.referenceColumns;
          if (rules && rules[key]) {
            // @ts-expect-error
            const higher = sum(weekSize.receipts.map((i) => valueGet(i, rules[key].higher)));
            // @ts-expect-error
            const lower = sum(weekSize.receipts.map((i) => valueGet(i, rules[key].lower)));
            if (lower !== 0) {
              // @ts-expect-error
              size[key] = {
                // @ts-expect-error
                value: size[key].value,
                // @ts-expect-error
                percentage: valueGet(size, rules[key].lower) / lower,
              };
            } else {
              // @ts-expect-error
              size[key] = {
                // @ts-expect-error
                value: size[key].value,
                // @ts-expect-error
                percentage: valueGet(size, rules[key].higher) / higher,
              };
            }
          } else {
            // @ts-expect-error
            size[key] = {
              // @ts-expect-error
              value: size[key].value,
              percentage: 1 / weekSize.receipts.length,
            };
          }
        });

        return {
          ...size,
          userAdj: {
            value: size.userAdj.value,
            percentage: percentageSystem,
          },
          onOrderRevision: {
            value: size.onOrderRevision.value,
            percentage: percentageOnOrder,
          },
          ...percValues,
        };
      });

      const initialTotal = this.calculateTotalFromSizes(newReceipts);
      return {
        weekNo: weekSize.weekNo,
        receipts: newReceipts,
        total: initialTotal,
      };
    });
  };

  getCalculatedTotal = (rowData: GridRow): number => {
    const { system, userAdj, onOrder, onOrderRevision } = rowData;

    if (!isNil(this.getFieldTotal('onOrderRevision'))) {
      return defaultTo(onOrderRevision?.value, 0);
    } else if (!isNil(this.getFieldTotal('onOrder'))) {
      return defaultTo(onOrder, 0);
    } else if (!isNil(this.getFieldTotal('userAdj'))) {
      return defaultTo(userAdj?.value, 0);
    }

    return defaultTo(system, 0);
  };

  buildGridRowData = (): GridRow[] => {
    const { sizes, activeWeekIndex, invalidSizes } = this.state;
    const receipts = sizes[activeWeekIndex]?.receipts || [];
    const invalidReceipts = invalidSizes[activeWeekIndex]?.receipts || [];
    const rows: GridRow[] = [];

    // Group 1: Color Details (with total row only)
    rows.push({
      dataPath: ['Color Details'],
      rowType: 'group',
      sizeattribute: 'Color Details',
      ...this.emptyTotalRow,
    } as GridRow);

    const colorTotal = this.calculateTotalFromSizes(receipts);
    rows.push({
      dataPath: ['Color Details', 'Total'],
      rowType: 'total',
      ...colorTotal,
    } as GridRow);

    // Group 2: Size Details (with individual size rows + total)
    rows.push({
      dataPath: ['Size Details'],
      rowType: 'group',
      sizeattribute: 'Size Details',
      ...this.emptyTotalRow,
    } as GridRow);

    receipts.forEach((receipt) => {
      rows.push({
        dataPath: ['Size Details', receipt.sizeattribute ?? receipt.name ?? ''],
        rowType: 'size',
        ...receipt,
      } as GridRow);
    });

    const sizeTotal = this.calculateTotalFromSizes(receipts);
    rows.push({
      dataPath: ['Size Details', 'Total'],
      rowType: 'total',
      ...sizeTotal,
    } as GridRow);

    // Group 3: Invalid Sizes (conditional)
    if (!isEmpty(invalidReceipts)) {
      rows.push({
        dataPath: ['Invalid Sizes'],
        rowType: 'group',
        sizeattribute: 'Invalid Sizes',
        ...this.emptyTotalRow,
      } as GridRow);

      invalidReceipts.forEach((receipt) => {
        rows.push({
          dataPath: ['Invalid Sizes', receipt.sizeattribute ?? receipt.name ?? ''],
          rowType: 'invalid-size',
          ...receipt,
        } as GridRow);
      });
    }

    return rows;
  };

  getTotalRowHighlightColor = (rowData: any): string => {
    let calculatedTotal = rowData.system;
    if (rowData.userAdj.value && rowData.userAdj.value >= 0) {
      calculatedTotal = rowData.userAdj.value;
    }
    if (rowData.onOrder && rowData.onOrder > 0) {
      calculatedTotal = rowData.onOrder;
    }
    if (rowData.onOrderRevision.value && rowData.onOrderRevision.value >= 0) {
      calculatedTotal = rowData.onOrderRevision.value;
    }
    return calculatedTotal === rowData.system ? 'inherit' : '#ff0000';
  };

  buildColumnDefs = () => {
    const { config } = this.state;
    const colDefs = config.columns.map((col) => {
      const colDef: ColDef = {
        headerName: col.text,
        field: col.dataIndex,
        width: REC_ADJ_COLUMN_WIDTH,
        headerClass: [styles.headerCell, wrappedHeaderStyle],
        valueGetter: (params: ValueGetterParams) => {
          if (!params.data || params.data.rowType === 'group') return null;
          const size = params.data;
          const dataIndex = col.dataIndex;
          const field = size[dataIndex];
          return isObject(field) ? get(size, `${dataIndex}.value`, null) : size[dataIndex];
        },
        /** editable callback for all rows
         * This is doing double duty for "regular" rows and "total" (total rows exist as regular rows in aggrid for now)
         * We're checking a few things here:
         * 1. Is the entire column/modal editalbe
         * 2. Is the size valid (not editable if not valid)
         * 3. Are we on one of the synthetic grouping rows (they're just for display)
         * 4. Then we figure out if we have a total for the given metric and allow editing onOrder or override
         * 5. Then we check for `editableByCalc`
         */
        editable: (params: EditableCallbackParams) => {
          const getDataFromKey = (dataIndex: string): GetDataCalculation => {
            if (params.data.hasOwnProperty(dataIndex)) {
              return {
                rowNodeFound: true,
                data: isObject(params.data[dataIndex])
                  ? get(params.data, `${dataIndex}.value`, null)
                  : params.data[dataIndex],
              };
            }
            return {
              rowNodeFound: true, // yes, this returns `true` despite it not being found, see INT-5382
              data: null,
            };
          };

          // early returns
          if (!params.data || params.data.rowType === 'group') return false;
          if (!this.props.isEditable) return false;
          if (!params.data.isValidSize && params.data.rowType !== 'total') return false; // `isValidSize` is not present on the synthetic 'total' rows
          if (col.dataIndex === 'final') return false; // `final` is never editable

          // #region editableByCalc
          // check if editableByCalc is configured, default to true if not so to can pass to the other conditions
          const isEditableByCalc: boolean = col.editableByCalc ? true : true;
          const calcOnSize = col.editableByCalc
            ? (executeCalculation(this.math, col.editableByCalc, getDataFromKey) ?? false)
            : true;

          // Use `calcOnSize` to determine if the field should be editable
          const isFieldEditable = isEditableByCalc && calcOnSize;
          // #endregion

          const onOrderTotal = this.getOnOrderTotal();

          // Column-specific editability
          if (col.dataIndex === 'userAdj') {
            const userAdjEditable = isNil(onOrderTotal);
            return userAdjEditable && isFieldEditable;
          }

          if (col.dataIndex === 'onOrderRevision') {
            const editable = !isNil(onOrderTotal);
            return editable && isFieldEditable;
          }

          if (col.editable === true) {
            return isFieldEditable;
          }

          return false;
        },
        valueSetter: (params: ValueSetterParams) => {
          if (!params.data || params.data.rowType === 'group') return false;

          // There is an issue after converting this to ag-grid, where the grid will send updates directly from the cell,
          // which triggers `setState`, which ultimately ends up in `DataGrid.componentDidUpdate()`
          // which updates gridData and calls `valueSetter` for the same cell, but without `newValue`.
          // This conditional attempts to control for that.
          if (params.newValue === null && params.oldValue === undefined) return false;

          const newValue = params.newValue;
          const isTotal = params.data.rowType === 'total';

          if (isTotal) {
            // Handle total row changes
            this.handleChangeTotalRow(col.dataIndex, newValue);
          } else {
            // Handle individual size row changes
            this.handleChangeReceipts(params.data.sizeattribute, col.dataIndex, newValue);
          }

          return false; // We'll refresh the grid manually
        },
        // We always use the same component for editing (for now)
        cellEditorSelector: (params: ICellEditorParams) => {
          // safety check
          if (!params.data || params.data.rowType === 'group') return undefined;

          // same component for all editing
          return {
            component: 'inputInteger',
            params: {
              nullable: true,
              editable: true, // Always set to true, as we gate on the aggrid `editable` callback, which if true allows the component to render
              valid: true,
            },
          };
        },
        // This is an internal S5 property used by datagrid
        // I don't know why this errors here and not in `EditableGrid.tsx`
        // @ts-ignore
        renderer: col.renderer ?? 'unknown',
        // Switch based on the currently selected component
        cellRendererSelector: (params: ICellRendererParams) => {
          if (!params.data || params.data.rowType === 'group') return undefined;

          switch (col.dataIndex) {
            case 'final': {
              return {
                component: (props: { value: string }) => <div>{props.value}</div>,
              };
            }
            default: {
              // Use existing renderer if specified
              if (col.renderer && col.renderer in Renderer) {
                const rendererFn = Renderer[col.renderer];
                return {
                  // @ts-expect-error
                  component: (props: any) => rendererFn(props.value),
                };
              }
              return undefined;
            }
          }
        },
        cellStyle: (params: CellClassParams): CellStyle => {
          if (!params.data) return {};

          const isTotal = params.data.rowType === 'total';
          const isUserAdj = col.dataIndex === 'userAdj';
          const isEditable =
            params.colDef.editable && typeof params.colDef.editable === 'function'
              ? params.colDef.editable(params)
              : params.colDef.editable;

          // Highlight if final differs from system
          if (col.dataIndex === 'final') {
            const totalHighlightColor = this.getTotalRowHighlightColor(params.data);
            return { color: isTotal ? totalHighlightColor : 'inherit', fontWeight: isTotal ? 600 : 400 };
          }

          return {
            fontWeight: isTotal || isUserAdj ? 600 : 400,
            backgroundColor: isEditable ? 'rgb(220, 243, 241)' : 'transparent',
            cursor: isEditable ? 'text' : 'initial', // to show editability
            textAlign: 'center',
          };
        },
        cellClass: (params: CellClassParams) => {
          if (!params.data) return '';
          const isTotal = params.data.rowType === 'total';
          const isEditable =
            params.colDef.editable && typeof params.colDef.editable === 'function'
              ? params.colDef.editable(params)
              : !!params.colDef.editable;
          return classes(
            isTotal ? styles.tableCellTotal : styles.tableCellBody,
            isEditable ? styles.cellEditingConstrain : '',
          );
        },
      };

      return colDef;
    });

    return colDefs;
  };

  getValue = () => {
    window.removeEventListener('resize', this.resizeOverlay);
    if (this.state.saved) {
      let onOrderAdjTtl = 0;
      let onOrderFinalTtl = 0;
      let shouldBeNullOnOrder = true;
      let rcptAdjTtl = 0;
      let rcptFinalTtl = 0;
      let shouldBeNullUserAdj = true;

      let editableKeyTtls: { [k: string]: number | null } = reduce(
        this.getNonStdEditableCols(),
        (acc, key) => {
          acc[key] = null;
          return acc;
        },
        {} as Record<string, any>,
      );

      // Calculates all of the useradj and onorders to get the value for grid
      this.state.sizes.forEach((size) => {
        const total = size.total;
        if (total) {
          // Test to see if it should return 0 or null
          if (!isNil(total.userAdj.value) && !isNaN(total.userAdj.value)) {
            shouldBeNullUserAdj = false;
          }
          // Actually adds everything
          if (total.userAdj.value && total.userAdj.value > 0) {
            rcptAdjTtl += total.userAdj.value;
            rcptFinalTtl += total.userAdj.value;
          } else {
            if (total.system && total.system > 0) {
              rcptFinalTtl += total.system;
            }
          }

          // Onorder version of above code
          if (!isNil(total.onOrderRevision.value)) {
            shouldBeNullOnOrder = false;
          }
          if (total.onOrderRevision.value && total.onOrderRevision.value > 0) {
            onOrderAdjTtl += total.onOrderRevision.value;
            onOrderFinalTtl += total.onOrderRevision.value;
          } else {
            if (total.onOrder && total.onOrder > 0) {
              onOrderFinalTtl += total.onOrder;
            }
          }
          editableKeyTtls = mapValues(editableKeyTtls, (ttl, key) => {
            // @ts-expect-error
            if (!isNil(total[key].value) && !isNaN(total[key].value)) {
              // @ts-expect-error
              return isNil(ttl) ? total[key].value : ttl + total[key].value;
            } else {
              return ttl;
            }
          });
        }
      });
      return {
        onOrderRevision: onOrderAdjTtl === 0 && shouldBeNullOnOrder ? null : onOrderAdjTtl,
        onOrderFinal: onOrderFinalTtl,
        userAdjRevision: rcptAdjTtl === 0 && shouldBeNullUserAdj ? null : rcptAdjTtl,
        rcptFinal: rcptFinalTtl,
        ...editableKeyTtls,
      };
    } else {
      let value = undefined;
      if (
        this.props.floorset &&
        this.props.node.data.extraData &&
        this.props.node.data.extraData[this.props.floorset]
      ) {
        value = this.props.node.data.extraData[this.props.floorset];
      }
      return value;
    }
  };

  calculateTotalFromSizes(sizes: SizeDetail[]): SizeDetail {
    const total = cloneDeep(this.emptyTotalRow);

    const editableKeys = this.state.config.columns.filter((k) => k.editable).map((k) => k.dataIndex);
    editableKeys.forEach((key) => {
      // @ts-expect-error
      total[key] = {
        value: null,
        percentage: 0.0,
      };
    });
    sizes.forEach((size) => {
      Object.keys(size).forEach((k) => {
        const key = k as unknown as keyof SizeDetail;
        if (key === ('userAdj' as const) || key === ('onOrderRevision' as const) || editableKeys.indexOf(key) >= 0) {
          // @ts-expect-error
          const valueToAdd = Number.isFinite(size[key].value) ? size[key].value : undefined;
          // @ts-expect-error
          const value = total[key].value;
          const newTotal = !isNil(valueToAdd)
            ? Number.isFinite(value) && !isNil(value)
              ? value + valueToAdd
              : valueToAdd
            : // @ts-expect-error
              total[key].value;

          // @ts-expect-error
          total[key].value = newTotal;
          // @ts-expect-error
          total[key].percentage = 1;
        } else if (!isNil(size[key]) && Number.isFinite(size[key])) {
          const valueToAdd2 = size[key] ? size[key] : 0;
          // @ts-expect-error
          const newTotal2 = total[key] ? total[key] + valueToAdd2 : valueToAdd2;
          // @ts-expect-error
          total[key] = newTotal2;
        }
      });
    });

    return total;
  }

  handleChangeReceipts = (sizeattribute: string, column: string, value: number | null) => {
    const weekSizes = cloneDeep(this.state.sizes);
    const receipts = weekSizes[this.state.activeWeekIndex].receipts;
    const sizeToChange: number = receipts.findIndex((size: SizeDetail) => {
      return size.sizeattribute === sizeattribute;
    });
    // @ts-expect-error
    receipts[sizeToChange][column].value = value;
    const newTotalRow = this.calculateTotalFromSizes(receipts);

    receipts.forEach((size) => {
      // @ts-expect-error
      const oldValue = size[column].value;
      // @ts-expect-error
      const newValue = oldValue / newTotalRow[column].value;
      // @ts-expect-error
      if (!Number.isFinite(newValue) && Number.isFinite(newTotalRow[column].value)) {
        // @ts-expect-error
        size[column].percentage = 0;
        // @ts-expect-error
        size[column].value = 0;
      }
      // @ts-expect-error
      size[column].percentage = newValue;
    });

    weekSizes[this.state.activeWeekIndex].receipts = receipts;
    weekSizes[this.state.activeWeekIndex].total = newTotalRow;
    this.setState(
      {
        sizes: weekSizes,
      },
      () => {
        // Refresh grid with new data
        const newRowData = this.buildGridRowData();
        this.gridApi?.updateGridOptions({ rowData: newRowData });
      },
    );
  };

  handleChangeTotalRow = (column: string, newTotalValue: number | null) => {
    // clone the sizes up front so we can mutate them safely
    const weekSizes = cloneDeep(this.state.sizes);
    const activeWeek = weekSizes[this.state.activeWeekIndex];
    const receipts = activeWeek.receipts;

    if (newTotalValue === 0 || !Number.isFinite(newTotalValue) || newTotalValue == null) {
      const init = this.calculateInitialPercentages(weekSizes);

      init[this.state.activeWeekIndex].receipts.map((x) => {
        // @ts-expect-error
        x[column].value = newTotalValue;
        return x;
      });
      // @ts-expect-error
      init[this.state.activeWeekIndex].total[column].value = newTotalValue;
      this.setState(
        {
          sizes: init,
        },
        () => {
          // Refresh grid with new data
          const newRowData = this.buildGridRowData();
          this.gridApi?.updateGridOptions({ rowData: newRowData });
        },
      );
      return;
    }

    // ensure this column has an overriding percentage calculation rule
    if (this.state.config.dataRules && this.state.config.dataRules.percentage.referenceColumns[column]) {
      const newValues = this.calculatePercentageHigherLower(
        this.state.config.dataRules.percentage,
        receipts,
        column,
        newTotalValue,
      );

      receipts.forEach((rec, ind) => {
        // @ts-expect-error
        rec[column].value = newValues[ind];
      });

      if (!isNil(activeWeek.total)) {
        // @ts-expect-error
        activeWeek.total[column].value = sum(newValues);
      }
      this.setState(
        {
          sizes: weekSizes,
        },
        () => {
          // Refresh grid with new data
          const newRowData = this.buildGridRowData();
          this.gridApi?.updateGridOptions({ rowData: newRowData });
        },
      );
      return;
    }

    interface Percents {
      percent: number; // warning: this can NaN
      index: number;
    }

    let currentTotal = 0;

    const percentageArray: Percents[] = [];
    receipts.forEach((size, index) => {
      // When a new total comes in, we need to re-calculate the size value of every size,
      // so we use the previous % contribution per size multiplied by the newTotal value to get the updated value.
      // Because this process runs for essentially all changes, float errors can be introduced, so we Math.Round()
      // and because units should be integers here, we then floor() them
      // @ts-expect-error
      const newSizeValue = Math.floor(Math.round(size[column].percentage * newTotalValue));
      currentTotal += newSizeValue || 0; // newSizeValue shouldn't be nullable here, this is for safety
      percentageArray.push({
        // @ts-expect-error
        percent: size[column].percentage,
        index: index,
      });
      // @ts-expect-error
      size[column].value = newSizeValue;
    });

    // To distribute the last couple items that might get lost, it takes the highest percentages and adds 1 to them
    percentageArray.sort((a, b) => b.percent - a.percent);
    percentageArray.forEach((percentObject) => {
      // Distribute to the highest percentages in cases where final total is less than intended total.
      if (newTotalValue && currentTotal < newTotalValue) {
        // @ts-expect-error
        receipts[percentObject.index][column].value = receipts[percentObject.index][column].value + 1;
        currentTotal += 1;
        // Undistribute from the highest percentages in cases where final total is greater than intended total.
      } else if (newTotalValue && currentTotal > newTotalValue) {
        // @ts-expect-error
        receipts[percentObject.index][column].value = receipts[percentObject.index][column].value - 1;
        currentTotal -= 1;
      }
    });

    // It shouldn't ever be possible to have a populated week *with* no total row. But I don't want to fight types.
    if (!isNil(activeWeek.total)) {
      // @ts-expect-error
      activeWeek.total[column].value = newTotalValue;
    }
    this.setState(
      {
        sizes: weekSizes,
      },
      () => {
        // Refresh grid with new data
        const newRowData = this.buildGridRowData();
        this.gridApi?.updateGridOptions({ rowData: newRowData });
      },
    );
  };

  getFieldTotal = (field: keyof SizeDetail) => {
    const { sizes, activeWeekIndex } = this.state;
    const sizesOnOrderV = sizes[activeWeekIndex].receipts.map((r) => get(r, field));
    if (findIndex(sizesOnOrderV, (oo) => !isNil(oo)) < 0) {
      return null;
    }
    return sum(sizesOnOrderV);
  };

  getOnOrderTotal = () => {
    return this.getFieldTotal('onOrder');
  };

  getFieldValue = (field: string, receiptIndex: number) => {
    const { sizes, activeWeekIndex } = this.state;
    // @ts-expect-error
    const value = sizes[activeWeekIndex].receipts[receiptIndex][field];
    return value;
  };

  getTotalValue = (field: keyof StaticTotals) => {
    const { staticTotals, activeWeekIndex } = this.state;
    return staticTotals[activeWeekIndex][field];
  };

  onGridReady = (event: GridReadyEvent) => {
    this.gridApi = event.api;
  };

  onClickWeekTab = (weekIndex: number) => {
    this.setState(
      {
        activeWeekIndex: weekIndex,
      },
      () => {
        // Rebuild and refresh grid data for new week
        const newRowData = this.buildGridRowData();
        this.gridApi?.updateGridOptions({ rowData: newRowData });
      },
    );
  };

  noDataJSX = () => {
    const { isSaving, isLoading, sizes } = this.state;
    return (
      <div className={styles.overlay} style={{ width: this.state.overlayWidth, height: this.state.overlayHeight }}>
        <div data-qa="ag-popover" className={styles.modalContainerForNoData}>
          <div data-qa="ag-popover-arrow" className={styles.modalArrowStyle} />
          <div className={styles.cardHeader}>Receipts Override Calculator</div>
          <div className={styles.tabContainer} />
          <div className={styles.containerNoData}>
            {isLoading || isSaving ? (
              <Overlay type="loading" visible={true} fitParent={true} />
            ) : (
              isEmpty(sizes) && (
                <div style={{ textAlign: 'center' }}>
                  <p>No sizes available.</p>
                  <p>The receipt period may be in the past and cannot be overridden.</p>
                </div>
              )
            )}
          </div>
          <div className={styles.actionsContainer}>
            <CardActions classes={MUIStyles.cardActions}>
              <RejectButton disabled={isSaving} onClick={this.cancel} />
            </CardActions>
          </div>
        </div>
      </div>
    );
  };

  getCoordinates = (size: SizeDetail) => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      product: (size as any).stylecolorsize,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      time: (size as any).week,
    };
  };
  save = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const serverData: any[] = [];
    this.state.sizes.forEach((size) => {
      size.receipts.forEach((receipt) => {
        serverData.push({
          coordinates: this.getCoordinates(receipt),
          ...this.convertSizeDetailToServerData(receipt),
        });
      });
    });
    // on save, we hold the modal in a loading state
    this.setState({ isSaving: true });
    // we use `finally` instead of then here to match old behavior of still closing modal
    // regardless of success of the update.
    ServiceContainer.pivotService.granularEditSubmitData(serverData).finally(() => {
      this.setState(
        {
          saved: true,
        },
        () => {
          this.props.stopEditing();
        },
      );
    });
  };

  cancel = () => {
    this.setState(
      {
        shouldSave: false,
      },
      () => this.props.stopEditing(),
    );
  };

  render() {
    // TODO: there needs to be separate rendering for "no sizes after fetch" and
    // "still fetching data"
    // Loading spinner is also used during update submission.

    const { sizes, isSaving, isLoading, activeWeekIndex } = this.state;
    if (isLoading || isSaving || !sizes || sizes.length === 0) {
      return this.noDataJSX();
    }

    const rowData = this.buildGridRowData();
    const columnDefs = this.buildColumnDefs();
    // adding 1 to account for the 'sizes' column
    const columnCount = this.state.config.columns.length + 1;
    return (
      <MuiThemeProvider theme={muiTheme}>
        <div
          className={`${styles.overlay}`}
          style={{ width: this.state.overlayWidth, height: this.state.overlayHeight }}
        >
          <div data-qa="ag-popover" className={styles.modalContainer} style={{ width: 'auto', maxWidth: '95vw' }}>
            <div className={styles.cardHeader}>Receipts Override Calculator</div>
            <div className={styles.tabContainer}>
              <Tabs
                value={activeWeekIndex}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="scrollable auto tabs"
                style={{ minHeight: 30 }}
              >
                {this.state.sizes.map((size, index) => {
                  const tabClass = activeWeekIndex === index ? styles.tabButtonActive : styles.tabButton;
                  return (
                    <button className={tabClass} onClick={() => this.onClickWeekTab(index)} key={size.weekNo}>
                      {size.weekNo}
                    </button>
                  );
                })}
              </Tabs>
            </div>

            <div
              className={styles.container}
              id="tra-grid-container"
              style={{
                height: `calc(${rowData.length ?? 10} * ${REC_ADJ_ROW_HEIGHT}px + 70px)`,
                width: `calc(${columnCount} * ${REC_ADJ_COLUMN_WIDTH}px + 100px)`,
              }}
            >
              <ExtendedDataGrid
                data={rowData}
                columnDefs={columnDefs}
                className={classes(
                  styles.dataGridContainer,
                  flowSheetGridStyles.dataGridStyle,
                  styles.modalGridOverrides,
                )}
                treeColumnDefinition={{
                  headerName: 'Sizes',
                  field: 'dataPath',
                  width: REC_ADJ_COLUMN_WIDTH + 30,
                  headerClass: [styles.headerCell, wrappedHeaderStyle],
                  cellClass: (params: CellClassParams) => {
                    if (!params.data) return '';
                    // 'agGroupCellRenderer' is load bearing for excel exports here
                    // see DataGrid.tsx `groupExcelStyles`, as that controls the rendering,
                    // and the class here binds the cell and the excel style together
                    // see INT-5894
                    return [styles.tableCellTotal, 'agGroupCellRenderer'];
                  },
                  colSpan: (params: ColSpanParams) => {
                    // Span all columns for group rows
                    if (params.data?.rowType === 'group') {
                      return columnCount;
                    }
                    return 1;
                  },
                  cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: (params: ICellRendererParams) => {
                      if (params.data?.rowType === 'group') {
                        return params.data.sizeattribute;
                      }
                      if (params.data?.rowType === 'total') {
                        return 'Total';
                      }
                      return params.data?.sizeattribute || params.value;
                    },
                  },
                }}
                loaded={true}
                singleClickEdit={true}
                rowHeight={REC_ADJ_ROW_HEIGHT}
                frameworkComponents={{
                  inputInteger: InputInteger,
                }}
                rowClassRules={{
                  [styles.headerSection]: (params: ICellRendererParams) => params.data?.rowType === 'group',
                  [styles.tableRow]: (params: ICellRendererParams) => params.data?.rowType !== 'group',
                  [styles.sectionText]: (params: ICellRendererParams) => params.data?.rowType === 'group',
                  [styles.tableCellTotal]: (params: ICellRendererParams) => params.data?.rowType === 'total',
                }}
                onGridReady={this.onGridReady}
                exportOptions={{
                  fileName: `Receipts_Override_${sizes[activeWeekIndex].weekNo}`,
                  processCellOverride: ({ node, column, value }: ProcessCellForExportParams) => {
                    if (column.getId() === 'ag-Grid-AutoColumn') {
                      return isArray(value) ? last(value) : value;
                    } else {
                      // partially copied from DataGrid.events
                      let cellRenderer = column.getColDef()['cellRenderer'];

                      if (isNil(cellRenderer) && isNumber(value)) {
                        cellRenderer = node ? node.data.formatter : undefined;
                      }
                    }
                  },
                }}
                extraAgGridProps={{
                  suppressMovableColumns: true,
                  suppressRowClickSelection: true,
                  stopEditingWhenCellsLoseFocus: true,
                  groupDefaultExpanded: -1,
                  headerHeight: 40,
                }}
              />
            </div>

            <div className={styles.actionsContainer}>
              <CardActions classes={MUIStyles.cardActions}>
                {this.props.isEditable && <AcceptButton disabled={this.state.isSaving} onClick={this.save} />}
                <RejectButton onClick={this.cancel} />
              </CardActions>
            </div>
          </div>
        </div>
      </MuiThemeProvider>
    );
  }
}
